import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  createCreatorRequest as createCreatorRequestService,
  getCreatorRequest as getCreatorRequestService,
  listCreatorRequests as listCreatorRequestsService,
  patchCreatorRequest as patchCreatorRequestService,
  runCreatorRequestDecision as runCreatorRequestDecisionService,
  recordCreatorRequestBusinessDecision as recordCreatorRequestBusinessDecisionService,
  linkCreatorRequestCampaign as linkCreatorRequestCampaignService,
  convertCreatorRequestToCampaign as convertCreatorRequestToCampaignService,
} from "../services/creatorRequestService";

import { isApiLimitError } from "../services/apiService";

const DEFAULT_UPGRADE_URL = "/pricing";
const DEFAULT_CREATOR_CHECK_METER = "search_credits";

const initialState = {
  items: [],
  selected: null,
  lastEvaluatedKey: null,

  listLoading: false,
  listError: null,
  listRequestId: null,

  createLoading: false,
  createError: null,
  createRequestId: null,

  getLoading: false,
  getError: null,
  getRequestId: null,

  patchLoading: false,
  patchError: null,
  patchRequestId: null,

  decisionLoading: false,
  decisionError: null,
  decisionRequestId: null,

  businessDecisionLoading: false,
  businessDecisionError: null,
  businessDecisionRequestId: null,

  campaignLinkLoading: false,
  campaignLinkError: null,
  campaignLinkRequestId: null,

  conversionLoading: false,
  conversionError: null,
  conversionRequestId: null,
};

const isObject = (value) =>
  Boolean(value && typeof value === "object" && !Array.isArray(value));

const isLimitPayload = (value) =>
  isObject(value) && String(value.type || "").toLowerCase() === "limit";

const toErrorMessage = (err, fallback) => {
  if (!err) return fallback;
  if (typeof err === "string") return err;

  return (
    err?.message ||
    err?.error?.message ||
    err?.payload?.message ||
    err?.payload?.error?.message ||
    fallback
  );
};

const errorMessageFromPayload = (payload, fallback) => {
  if (!payload) return "";
  if (typeof payload === "string") return payload;

  return (
    payload?.message ||
    payload?.error?.message ||
    payload?.payload?.message ||
    payload?.payload?.error?.message ||
    fallback
  );
};

const toLimitErrorPayload = (
  err,
  fallback,
  fallbackMeter = DEFAULT_CREATOR_CHECK_METER
) => ({
  type: "limit",
  kind: err?.kind || "limit_exceeded",
  message: err?.message || fallback,
  status: err?.status || 402,
  code: err?.code || "LIMIT_EXCEEDED",
  reason: err?.reason || err?.code || "LIMIT_EXCEEDED",
  meter: err?.meter || fallbackMeter,
  used: err?.used,
  limit: err?.limit,
  remaining: err?.remaining,
  resetAt: err?.resetAt || err?.reset_at || null,
  upgradeRequired: Boolean(err?.upgradeRequired ?? err?.upgrade_required ?? true),
  upgradeUrl: err?.upgradeUrl || err?.upgrade_url || DEFAULT_UPGRADE_URL,
  payload: err?.payload || null,
});

const toRejectedPayload = (
  err,
  fallback,
  fallbackMeter = DEFAULT_CREATOR_CHECK_METER
) => {
  if (isApiLimitError(err)) {
    return toLimitErrorPayload(err, fallback, fallbackMeter);
  }

  if (isLimitPayload(err)) {
    return {
      ...err,
      message: err.message || fallback,
      meter: err.meter || fallbackMeter,
      upgradeUrl: err.upgradeUrl || DEFAULT_UPGRADE_URL,
    };
  }

  return {
    type: "error",
    message: toErrorMessage(err, fallback),
  };
};

const normalizeCreatorRequest = (raw) => {
  if (!raw || typeof raw !== "object") return null;

  return {
    ...raw,
    requestId: raw.requestId || raw.request_id || "",
    tenantId: raw.tenantId || raw.tenant_id || "",
    businessId: raw.businessId || raw.business_id || "",
    userId: raw.userId || raw.user_id || "",

    creatorId: raw.creatorId ?? raw.creator_id ?? null,
    creatorHandle: raw.creatorHandle ?? raw.creator_handle ?? null,
    creatorProfileUrl: raw.creatorProfileUrl ?? raw.creator_profile_url ?? null,

    inboundMessage: raw.inboundMessage ?? raw.inbound_message ?? null,
    requestedFee: raw.requestedFee ?? raw.requested_fee ?? null,
    requestedGift: raw.requestedGift ?? raw.requested_gift ?? null,
    proposedDeliverables:
      raw.proposedDeliverables || raw.proposed_deliverables || [],

    linkedCreatorCheckId:
      raw.linkedCreatorCheckId ?? raw.linked_creator_check_id ?? null,

    recommendedAction: raw.recommendedAction ?? raw.recommended_action ?? null,
    recommendedCollaborationType:
      raw.recommendedCollaborationType ??
      raw.recommended_collaboration_type ??
      null,

    decisionSummary: raw.decisionSummary ?? raw.decision_summary ?? null,
    businessDecision: raw.businessDecision ?? raw.business_decision ?? null,
    decisionNote: raw.decisionNote ?? raw.decision_note ?? null,

    campaignId: raw.campaignId ?? raw.campaign_id ?? null,
    offerId: raw.offerId ?? raw.offer_id ?? null,
    inviteId: raw.inviteId ?? raw.invite_id ?? null,

    staffNotes: raw.staffNotes ?? raw.staff_notes ?? null,

    createdAt: raw.createdAt || raw.created_at || "",
    updatedAt: raw.updatedAt || raw.updated_at || "",
  };
};

const normalizeListResponse = (raw) => {
  if (!raw || typeof raw !== "object") {
    return {
      items: [],
      lastEvaluatedKey: null,
    };
  }

  const items = Array.isArray(raw.items)
    ? raw.items.map(normalizeCreatorRequest).filter(Boolean)
    : [];

  return {
    items,
    lastEvaluatedKey: raw.lastEvaluatedKey || raw.last_evaluated_key || null,
  };
};

const upsertItem = (items, item) => {
  if (!item?.requestId) return items;

  const index = items.findIndex((existing) => existing.requestId === item.requestId);

  if (index < 0) {
    return [item, ...items];
  }

  const next = [...items];
  next[index] = {
    ...next[index],
    ...item,
  };

  return next;
};

export const createCreatorRequestAsync = createAsyncThunk(
  "creatorRequests/create",
  async (payload, { rejectWithValue }) => {
    try {
      const resp = await createCreatorRequestService(payload);
      return normalizeCreatorRequest(resp);
    } catch (error) {
      return rejectWithValue(
        toRejectedPayload(error, "Failed to create creator request.")
      );
    }
  }
);

export const getCreatorRequestAsync = createAsyncThunk(
  "creatorRequests/get",
  async ({ requestId }, { rejectWithValue }) => {
    try {
      const resp = await getCreatorRequestService(requestId);
      return normalizeCreatorRequest(resp);
    } catch (error) {
      return rejectWithValue(
        toRejectedPayload(error, "Failed to load creator request.")
      );
    }
  }
);

export const listCreatorRequestsAsync = createAsyncThunk(
  "creatorRequests/list",
  async (
    { businessId, limit = 50, lastEvaluatedKey = null } = {},
    { rejectWithValue }
  ) => {
    try {
      const resp = await listCreatorRequestsService({
        businessId,
        limit,
        lastEvaluatedKey,
      });
      return normalizeListResponse(resp);
    } catch (error) {
      return rejectWithValue(
        toRejectedPayload(error, "Failed to load creator requests.")
      );
    }
  }
);

export const patchCreatorRequestAsync = createAsyncThunk(
  "creatorRequests/patch",
  async ({ requestId, payload }, { rejectWithValue }) => {
    try {
      const resp = await patchCreatorRequestService(requestId, payload);
      return normalizeCreatorRequest(resp);
    } catch (error) {
      return rejectWithValue(
        toRejectedPayload(error, "Failed to update creator request.")
      );
    }
  }
);

export const runCreatorRequestDecisionAsync = createAsyncThunk(
  "creatorRequests/runDecision",
  async ({ requestId, payload }, { rejectWithValue }) => {
    try {
      const resp = await runCreatorRequestDecisionService(requestId, payload);
      return normalizeCreatorRequest(resp);
    } catch (error) {
      return rejectWithValue(
        toRejectedPayload(
          error,
          "Failed to run creator decision.",
          DEFAULT_CREATOR_CHECK_METER
        )
      );
    }
  }
);

export const recordCreatorRequestBusinessDecisionAsync = createAsyncThunk(
  "creatorRequests/businessDecision",
  async ({ requestId, payload }, { rejectWithValue }) => {
    try {
      const resp = await recordCreatorRequestBusinessDecisionService(
        requestId,
        payload
      );
      return normalizeCreatorRequest(resp);
    } catch (error) {
      return rejectWithValue(
        toRejectedPayload(error, "Failed to record business decision.")
      );
    }
  }
);

export const linkCreatorRequestCampaignAsync = createAsyncThunk(
  "creatorRequests/campaignLink",
  async ({ requestId, payload }, { rejectWithValue }) => {
    try {
      const resp = await linkCreatorRequestCampaignService(requestId, payload);
      return normalizeCreatorRequest(resp);
    } catch (error) {
      return rejectWithValue(
        toRejectedPayload(error, "Failed to link creator request to campaign.")
      );
    }
  }
);

export const convertCreatorRequestToCampaignAsync = createAsyncThunk(
  "creatorRequests/convertToCampaign",
  async ({ requestId, payload }, { rejectWithValue }) => {
    try {
      const resp = await convertCreatorRequestToCampaignService(
        requestId,
        payload
      );
      return normalizeCreatorRequest(resp);
    } catch (error) {
      return rejectWithValue(
        toRejectedPayload(
          error,
          "Failed to create campaign from creator request."
        )
      );
    }
  }
);

const creatorRequestSlice = createSlice({
  name: "creatorRequests",
  initialState,
  reducers: {
    clearCreatorRequestErrors(state) {
      state.listError = null;
      state.createError = null;
      state.getError = null;
      state.patchError = null;
      state.decisionError = null;
      state.businessDecisionError = null;
      state.campaignLinkError = null;
      state.conversionError = null;
    },

    clearSelectedCreatorRequest(state) {
      state.selected = null;
      state.getError = null;
      state.patchError = null;
      state.decisionError = null;
      state.businessDecisionError = null;
      state.campaignLinkError = null;
      state.conversionError = null;
    },

    resetCreatorRequestState() {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(listCreatorRequestsAsync.pending, (state, action) => {
        state.listLoading = true;
        state.listError = null;
        state.listRequestId = action.meta.requestId;
      })
      .addCase(listCreatorRequestsAsync.fulfilled, (state, action) => {
        if (state.listRequestId !== action.meta.requestId) return;

        state.listLoading = false;
        state.listRequestId = null;
        state.listError = null;
        state.items = action.payload?.items || [];
        state.lastEvaluatedKey = action.payload?.lastEvaluatedKey || null;
      })
      .addCase(listCreatorRequestsAsync.rejected, (state, action) => {
        if (state.listRequestId !== action.meta.requestId) return;

        state.listLoading = false;
        state.listRequestId = null;
        state.listError =
          action.payload ||
          action.error?.message ||
          "Failed to load creator requests.";
      })

      .addCase(createCreatorRequestAsync.pending, (state, action) => {
        state.createLoading = true;
        state.createError = null;
        state.createRequestId = action.meta.requestId;
      })
      .addCase(createCreatorRequestAsync.fulfilled, (state, action) => {
        if (state.createRequestId !== action.meta.requestId) return;

        state.createLoading = false;
        state.createRequestId = null;
        state.createError = null;

        const item = action.payload || null;
        state.selected = item;
        state.items = upsertItem(state.items, item);
      })
      .addCase(createCreatorRequestAsync.rejected, (state, action) => {
        if (state.createRequestId !== action.meta.requestId) return;

        state.createLoading = false;
        state.createRequestId = null;
        state.createError =
          action.payload ||
          action.error?.message ||
          "Failed to create creator request.";
      })

      .addCase(getCreatorRequestAsync.pending, (state, action) => {
        state.getLoading = true;
        state.getError = null;
        state.getRequestId = action.meta.requestId;
      })
      .addCase(getCreatorRequestAsync.fulfilled, (state, action) => {
        if (state.getRequestId !== action.meta.requestId) return;

        state.getLoading = false;
        state.getRequestId = null;
        state.getError = null;

        const item = action.payload || null;
        state.selected = item;
        state.items = upsertItem(state.items, item);
      })
      .addCase(getCreatorRequestAsync.rejected, (state, action) => {
        if (state.getRequestId !== action.meta.requestId) return;

        state.getLoading = false;
        state.getRequestId = null;
        state.getError =
          action.payload ||
          action.error?.message ||
          "Failed to load creator request.";
      })

      .addCase(patchCreatorRequestAsync.pending, (state, action) => {
        state.patchLoading = true;
        state.patchError = null;
        state.patchRequestId = action.meta.requestId;
      })
      .addCase(patchCreatorRequestAsync.fulfilled, (state, action) => {
        if (state.patchRequestId !== action.meta.requestId) return;

        state.patchLoading = false;
        state.patchRequestId = null;
        state.patchError = null;

        const item = action.payload || null;
        state.selected = item;
        state.items = upsertItem(state.items, item);
      })
      .addCase(patchCreatorRequestAsync.rejected, (state, action) => {
        if (state.patchRequestId !== action.meta.requestId) return;

        state.patchLoading = false;
        state.patchRequestId = null;
        state.patchError =
          action.payload ||
          action.error?.message ||
          "Failed to update creator request.";
      })

      .addCase(runCreatorRequestDecisionAsync.pending, (state, action) => {
        state.decisionLoading = true;
        state.decisionError = null;
        state.decisionRequestId = action.meta.requestId;
      })
      .addCase(runCreatorRequestDecisionAsync.fulfilled, (state, action) => {
        if (state.decisionRequestId !== action.meta.requestId) return;

        state.decisionLoading = false;
        state.decisionRequestId = null;
        state.decisionError = null;

        const item = action.payload || null;
        state.selected = item;
        state.items = upsertItem(state.items, item);
      })
      .addCase(runCreatorRequestDecisionAsync.rejected, (state, action) => {
        if (state.decisionRequestId !== action.meta.requestId) return;

        state.decisionLoading = false;
        state.decisionRequestId = null;
        state.decisionError =
          action.payload ||
          action.error?.message ||
          "Failed to run creator decision.";
      })

      .addCase(recordCreatorRequestBusinessDecisionAsync.pending, (state, action) => {
        state.businessDecisionLoading = true;
        state.businessDecisionError = null;
        state.businessDecisionRequestId = action.meta.requestId;
      })
      .addCase(
        recordCreatorRequestBusinessDecisionAsync.fulfilled,
        (state, action) => {
          if (state.businessDecisionRequestId !== action.meta.requestId) return;

          state.businessDecisionLoading = false;
          state.businessDecisionRequestId = null;
          state.businessDecisionError = null;

          const item = action.payload || null;
          state.selected = item;
          state.items = upsertItem(state.items, item);
        }
      )
      .addCase(
        recordCreatorRequestBusinessDecisionAsync.rejected,
        (state, action) => {
          if (state.businessDecisionRequestId !== action.meta.requestId) return;

          state.businessDecisionLoading = false;
          state.businessDecisionRequestId = null;
          state.businessDecisionError =
            action.payload ||
            action.error?.message ||
            "Failed to record business decision.";
        }
      )

      .addCase(linkCreatorRequestCampaignAsync.pending, (state, action) => {
        state.campaignLinkLoading = true;
        state.campaignLinkError = null;
        state.campaignLinkRequestId = action.meta.requestId;
      })
      .addCase(linkCreatorRequestCampaignAsync.fulfilled, (state, action) => {
        if (state.campaignLinkRequestId !== action.meta.requestId) return;

        state.campaignLinkLoading = false;
        state.campaignLinkRequestId = null;
        state.campaignLinkError = null;

        const item = action.payload || null;
        state.selected = item;
        state.items = upsertItem(state.items, item);
      })
      .addCase(linkCreatorRequestCampaignAsync.rejected, (state, action) => {
        if (state.campaignLinkRequestId !== action.meta.requestId) return;

        state.campaignLinkLoading = false;
        state.campaignLinkRequestId = null;
        state.campaignLinkError =
          action.payload ||
          action.error?.message ||
          "Failed to link creator request to campaign.";
      })

      .addCase(convertCreatorRequestToCampaignAsync.pending, (state, action) => {
        state.conversionLoading = true;
        state.conversionError = null;
        state.conversionRequestId = action.meta.requestId;
      })
      .addCase(convertCreatorRequestToCampaignAsync.fulfilled, (state, action) => {
        if (state.conversionRequestId !== action.meta.requestId) return;

        state.conversionLoading = false;
        state.conversionRequestId = null;
        state.conversionError = null;

        const item = action.payload || null;
        state.selected = item;
        state.items = upsertItem(state.items, item);
      })
      .addCase(convertCreatorRequestToCampaignAsync.rejected, (state, action) => {
        if (state.conversionRequestId !== action.meta.requestId) return;

        state.conversionLoading = false;
        state.conversionRequestId = null;
        state.conversionError =
          action.payload ||
          action.error?.message ||
          "Failed to create campaign from creator request.";
      });
  },
});

export const {
  clearCreatorRequestErrors,
  clearSelectedCreatorRequest,
  resetCreatorRequestState,
} = creatorRequestSlice.actions;

export const selectCreatorRequests = (state) =>
  state.creatorRequests?.items || [];

export const selectSelectedCreatorRequest = (state) =>
  state.creatorRequests?.selected || null;

export const selectCreatorRequestsLastEvaluatedKey = (state) =>
  state.creatorRequests?.lastEvaluatedKey || null;

export const selectCreatorRequestsListLoading = (state) =>
  state.creatorRequests?.listLoading || false;

export const selectCreatorRequestsCreateLoading = (state) =>
  state.creatorRequests?.createLoading || false;

export const selectCreatorRequestGetLoading = (state) =>
  state.creatorRequests?.getLoading || false;

export const selectCreatorRequestPatchLoading = (state) =>
  state.creatorRequests?.patchLoading || false;

export const selectCreatorRequestDecisionLoading = (state) =>
  state.creatorRequests?.decisionLoading || false;

export const selectCreatorRequestBusinessDecisionLoading = (state) =>
  state.creatorRequests?.businessDecisionLoading || false;

export const selectCreatorRequestCampaignLinkLoading = (state) =>
  state.creatorRequests?.campaignLinkLoading || false;

export const selectCreatorRequestConversionLoading = (state) =>
  state.creatorRequests?.conversionLoading || false;

export const selectCreatorRequestsListError = (state) =>
  state.creatorRequests?.listError || null;

export const selectCreatorRequestCreateError = (state) =>
  state.creatorRequests?.createError || null;

export const selectCreatorRequestGetError = (state) =>
  state.creatorRequests?.getError || null;

export const selectCreatorRequestPatchError = (state) =>
  state.creatorRequests?.patchError || null;

export const selectCreatorRequestDecisionError = (state) =>
  state.creatorRequests?.decisionError || null;

export const selectCreatorRequestBusinessDecisionError = (state) =>
  state.creatorRequests?.businessDecisionError || null;

export const selectCreatorRequestCampaignLinkError = (state) =>
  state.creatorRequests?.campaignLinkError || null;

export const selectCreatorRequestConversionError = (state) =>
  state.creatorRequests?.conversionError || null;

export const selectCreatorRequestsListErrorMessage = (state) =>
  errorMessageFromPayload(
    state.creatorRequests?.listError,
    "Failed to load creator requests."
  );

export const selectCreatorRequestCreateErrorMessage = (state) =>
  errorMessageFromPayload(
    state.creatorRequests?.createError,
    "Failed to create creator request."
  );

export const selectCreatorRequestGetErrorMessage = (state) =>
  errorMessageFromPayload(
    state.creatorRequests?.getError,
    "Failed to load creator request."
  );

export const selectCreatorRequestPatchErrorMessage = (state) =>
  errorMessageFromPayload(
    state.creatorRequests?.patchError,
    "Failed to update creator request."
  );

export const selectCreatorRequestDecisionErrorMessage = (state) =>
  errorMessageFromPayload(
    state.creatorRequests?.decisionError,
    "Failed to run creator decision."
  );

export const selectCreatorRequestBusinessDecisionErrorMessage = (state) =>
  errorMessageFromPayload(
    state.creatorRequests?.businessDecisionError,
    "Failed to record business decision."
  );

export const selectCreatorRequestCampaignLinkErrorMessage = (state) =>
  errorMessageFromPayload(
    state.creatorRequests?.campaignLinkError,
    "Failed to link creator request to campaign."
  );

export const selectCreatorRequestConversionErrorMessage = (state) =>
  errorMessageFromPayload(
    state.creatorRequests?.conversionError,
    "Failed to create campaign from creator request."
  );

export const selectCreatorRequestLimitError = (state) => {
  const candidates = [
    state.creatorRequests?.decisionError,
    state.creatorRequests?.createError,
    state.creatorRequests?.patchError,
    state.creatorRequests?.businessDecisionError,
    state.creatorRequests?.campaignLinkError,
    state.creatorRequests?.conversionError,
  ];

  return candidates.find(isLimitPayload) || null;
};

export default creatorRequestSlice.reducer;